import React, { useEffect, useState } from "react";
import { api, LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, MessageSquare, CheckCircle2, XCircle } from "lucide-react";

export default function SettingsPage() {
  const [form, setForm] = useState({
    logo_url: "", organization_name: "", tagline: "",
    tv_marquee: "", voice_announcement: true, privacy_mode: true,
    max_recalls: 1,
  });
  const [loading, setLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState({ sms_enabled: false });
  const [smsTo, setSmsTo] = useState("");
  const [smsBusy, setSmsBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm((f) => ({ ...f, ...r.data })));
    api.get("/system/status").then((r) => setSystemStatus(r.data)).catch(() => {});
  }, []);

  const testSms = async () => {
    if (!smsTo.trim()) return;
    setSmsBusy(true);
    try {
      const { data } = await api.post("/sms/test", { to: smsTo, body: "Test MAMA — votre tour approche." });
      if (data.ok) toast.success(`SMS envoyé (${data.sid})`);
      else toast.error(`SMS échec: ${data.error}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSmsBusy(false); }
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.patch("/settings", form);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-display font-bold text-3xl">Paramètres système</h1>
        <p className="text-slate-500 text-sm mt-1">Identité visuelle, messages d'affichage et options globales.</p>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader><CardTitle className="font-display text-lg">Identité visuelle</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6">
            <img src={form.logo_url || LOGO_URL} alt="Logo" className="w-20 h-20 border border-slate-200 rounded-lg p-2" />
            <div className="flex-1 space-y-1">
              <Label>URL du logo</Label>
              <Input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" data-testid="settings-logo-url" />
            </div>
          </div>
          <div><Label>Nom de l'organisation</Label><Input value={form.organization_name || ""} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} /></div>
          <div><Label>Slogan</Label><Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader><CardTitle className="font-display text-lg">Écran d'affichage TV</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Message défilant (marquee)</Label>
            <Textarea value={form.tv_marquee || ""} onChange={(e) => setForm({ ...form, tv_marquee: e.target.value })} rows={3} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Annonce vocale</div>
              <div className="text-sm text-slate-500">Lit le numéro de ticket lors de l'appel (synthèse vocale française).</div>
            </div>
            <Switch checked={!!form.voice_announcement} onCheckedChange={(v) => setForm({ ...form, voice_announcement: v })} data-testid="settings-voice-toggle" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader><CardTitle className="font-display text-lg">Sécurité et confidentialité</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Mode confidentialité</div>
              <div className="text-sm text-slate-500">Masquer les noms et données personnelles sur les écrans publics.</div>
            </div>
            <Switch checked={!!form.privacy_mode} onCheckedChange={(v) => setForm({ ...form, privacy_mode: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label>Nombre maximum de rappels par ticket</Label>
              <Input type="number" min={0} max={5}
                value={form.max_recalls ?? 1}
                onChange={(e) => setForm({ ...form, max_recalls: parseInt(e.target.value || "0") })}
                data-testid="settings-max-recalls" />
            </div>
            <p className="text-xs text-slate-500 pb-2">
              Un agent peut rappeler le même ticket ce nombre de fois maximum avant qu'il ne soit définitivement marqué.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader>
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Notifications SMS (Twilio)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            {systemStatus.sms_enabled ? (
              <><CheckCircle2 className="w-4 h-4 text-[#16A34A]" />
                <span className="text-sm text-slate-700">SMS Twilio configuré et actif</span></>
            ) : (
              <><XCircle className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">Twilio non configuré — renseignez TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN et TWILIO_PHONE_NUMBER dans <code className="px-1 bg-slate-100 rounded">/app/backend/.env</code>, puis redémarrez le backend.</span></>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Lorsque activé, les clients ayant fourni leur numéro reçoivent un SMS quand leur ticket
            est à la position N°{`{SMS_NOTIFY_POSITION=3}`} dans la file.
          </p>
          <div className="flex gap-2">
            <Input value={smsTo} onChange={(e) => setSmsTo(e.target.value)}
                   placeholder="+261 34 12 345 67"
                   data-testid="sms-test-to" />
            <Button onClick={testSms} disabled={smsBusy || !smsTo.trim()}
                    className="bg-slate-900 hover:bg-slate-800" data-testid="sms-test-button">
              {smsBusy ? "Envoi…" : "Tester"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={loading} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="settings-save">
          <Save className="w-4 h-4 mr-2" /> {loading ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}
